import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Button, EmptyState, QueryStateBoundary } from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { useQuotes } from "@/api/hooks/useQuotes";
import { usePackages } from "@/api/hooks/usePackages";
import { useInquiry } from "@/api/hooks/useInquiries";
import { QUOTE_STATUS_BADGE_VARIANT, QUOTE_STATUS_LABELS } from "@/api/types/quote";
import type { Quote } from "@/api/types/quote";
import { InquiryPickerDialog } from "./InquiryPickerDialog";
import { QuoteBuilderDialog } from "./QuoteBuilderDialog";

export function QuotesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: quotes, isLoading, error, refetch } = useQuotes();
  const { data: packages } = usePackages();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [builderInquiryId, setBuilderInquiryId] = useState<string | null>(null);

  // Reachable from an inquiry: the InquiryDetailDialog's "Create quote"
  // button navigates here with ?inquiry=<id>, which auto-opens the builder
  // for that inquiry without requiring the picker.
  useEffect(() => {
    const inquiryParam = searchParams.get("inquiry");
    if (inquiryParam) {
      setBuilderInquiryId(inquiryParam);
      const next = new URLSearchParams(searchParams);
      next.delete("inquiry");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handlePickInquiry(inquiryId: string) {
    setPickerOpen(false);
    setBuilderInquiryId(inquiryId);
  }

  function handleQuoteSaved(quote: Quote) {
    setBuilderInquiryId(null);
    navigate(`/quotes/${quote.id}`);
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Quotes</h1>
        <Button onClick={() => setPickerOpen(true)}>Start a quote</Button>
      </div>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {!quotes || quotes.length === 0 ? (
          <EmptyState
            icon="📄"
            title="No quotes yet"
            description="Build a quote from any inquiry — Signature is pre-selected by default."
            action={{ label: "Start a quote", onClick: () => setPickerOpen(true) }}
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border bg-bg-card">
            {quotes.map((quote) => (
              <QuoteListRow
                key={quote.id}
                quote={quote}
                packageName={packages?.find((p) => p.id === quote.package_id)?.name}
                onOpen={() => navigate(`/quotes/${quote.id}`)}
              />
            ))}
          </ul>
        )}
      </QueryStateBoundary>

      <InquiryPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickInquiry}
      />

      {builderInquiryId && (
        <QuoteBuilderDialog
          open={!!builderInquiryId}
          onClose={() => setBuilderInquiryId(null)}
          inquiryId={builderInquiryId}
          onSaved={handleQuoteSaved}
        />
      )}
    </div>
  );
}

interface QuoteListRowProps {
  quote: Quote;
  packageName?: string;
  onOpen: () => void;
}

function QuoteListRow({ quote, packageName, onOpen }: QuoteListRowProps) {
  const { data: inquiry } = useInquiry(quote.inquiry_id);

  return (
    <li>
      <button
        type="button"
        data-quote-id={quote.id}
        onClick={onOpen}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-bg-hover"
      >
        <div>
          <p className="font-medium text-text-primary">
            {inquiry?.contact_name ?? "Quote"}
            {packageName ? ` — ${packageName}` : ""}
          </p>
          <p className="text-xs text-text-muted">{centsToUsd(quote.total_cents)} total</p>
        </div>
        <Badge variant={QUOTE_STATUS_BADGE_VARIANT[quote.status]}>
          {QUOTE_STATUS_LABELS[quote.status]}
        </Badge>
      </button>
    </li>
  );
}
